import type { ResolvedCheckpoint } from '../../logic/checkpointUtils'
import AppIcon from '../icons/AppIcon'
import StatusChip from '../primitives/StatusChip'
import styles from './CheckpointCard.module.css'

interface CheckpointCardProps {
  checkpoint: ResolvedCheckpoint
  gated?: boolean
  current?: boolean
  onClick?: () => void
}

export default function CheckpointCard({ checkpoint, gated, current, onClick }: CheckpointCardProps) {
  const { label, city, status, clearedDay, away } = checkpoint
  const chipVariant = gated ? 'next' : status === 'finish' ? 'finish' : status

  const meta =
    status === 'done' && clearedDay
      ? `${checkpoint.rms.toLocaleString()} RMs · Cleared Day ${clearedDay}`
      : gated
        ? `${checkpoint.rms.toLocaleString()} RMs · You're here`
        : away !== undefined
          ? (
              <>
                {checkpoint.rms.toLocaleString()} RMs · <b>{away.toLocaleString()}</b> away
              </>
            )
          : `${checkpoint.rms.toLocaleString()} RMs`

  const dimmed = !gated && (status === 'locked' || (status === 'finish' && away !== undefined))

  const className = [
    styles.card,
    gated && styles.gated,
    !gated && status === 'next' && styles.next,
    dimmed && (status === 'locked' ? styles.locked : styles.finish),
  ]
    .filter(Boolean)
    .join(' ')

  const content = (
    <>
      <div className={styles.top}>
        <span className={styles.number}>{label}</span>
        {gated ? (
          <span className={styles.actionChip}>
            <AppIcon icon="mdi:lock" size={14} />
            Action needed
          </span>
        ) : (
          <StatusChip variant={chipVariant} />
        )}
      </div>
      <div className={styles.city}>{city}</div>
      <div className={styles.meta}>{meta}</div>
      {gated && (
        <div className={styles.gatedAction}>
          <span>Complete team challenge</span>
          <AppIcon icon="mdi:chevron-right" size={18} />
        </div>
      )}
    </>
  )

  const dataAttrs = {
    'data-testid': 'checkpoint-card',
    'data-status': status,
    ...(current && { 'data-current': true }),
  }

  if (onClick) {
    return (
      <button type="button" className={className} {...dataAttrs} onClick={onClick}>
        {content}
      </button>
    )
  }

  return (
    <article className={className} {...dataAttrs}>
      {content}
    </article>
  )
}
